import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabDataCenters, useLabs, useSaveLabRow } from '../../hooks/lab-hooks';
import {
  Lab,
  LabConfiguration,
  LabOption,
  LabSize,
  labConfigurationSlug,
  labDataCenterCode,
  labSizeLabel,
  labSlugify,
  LAB_SIZES,
  LAB_TABLES,
} from '../../support/labService';
import LabDcloudLookupPanel, { LabDcloudPick } from './LabDcloudLookupPanel';
import LabEditorModal from './LabEditorModal';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';

export type LabConfigurationEditorModalProps = {
  open: boolean;
  configuration: LabConfiguration | null;
  /** Seeds the lab for a new row, so publishing from inside a lab does not ask again. */
  defaultLabId?: number;
  /**
   * Every publication already created, used to catch a duplicate (lab, data centre) before the
   * API does. The backend has the unique constraint; this only turns a 400 into a sentence.
   */
  existing?: LabConfiguration[];
  onClose: () => void;
  onSaved?: (configuration: LabConfiguration) => void;
};

const EMPTY: Partial<LabConfiguration> = {
  name: '',
  slug: '',
  description: '',
  demo_uid: null,
  demo_name: '',
  topology_uid: '',
  template_topology_uid: '',
  size: 'medium',
  enabled: true,
  metadata: {},
};

/**
 * Publish a lab into a data centre: the lab, the data centre, a name, and the dCloud lab it
 * provisions from.
 *
 * The reason this row exists is that a dCloud lab id is not global: the same lab has a different
 * demo id in RTP and in London. Before this table the only way to express that was to clone the
 * content per data centre, and the copies drifted.
 *
 * It binds a **lab**, not a scenario. The whole lab maps to one topology per data centre - every
 * section it is composed of runs in that one pod - so the publication hangs off the lab and the
 * scenarios stay data-centre agnostic, which is what lets a scenario be reused by another lab
 * published somewhere else entirely.
 *
 * Only the things a person actually decides are asked for - which lab, which data centre, what to
 * call it. The dCloud ids come from the lookup below, because none of them are memorable and two
 * of the three are only correct in one data centre, and the slug is derived rather than typed. A
 * typed slug is not a label: events, runs and injections refer to this row by it, so every
 * hand-entered one is a chance to record a spelling something downstream already has differently,
 * in exchange for a field nobody wants to fill in. On an existing row it is shown and never
 * recomputed - renaming must not orphan what already points at it.
 *
 * The hand-entry fields survive behind a disclosure, because a data centre whose catalog cannot
 * be read - no dCloud link, or a code we do not recognise - must still be publishable rather
 * than blocked on a lookup that cannot answer.
 */
const LabConfigurationEditorModal: React.FC<LabConfigurationEditorModalProps> = ({
  open,
  configuration,
  defaultLabId,
  existing = [],
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabConfiguration>>(EMPTY);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Stops the suggested name from overwriting a typed one when the data centre changes.
  const [nameTouched, setNameTouched] = useState(false);
  // Same guard for the demo: once somebody has picked or typed one, the lab stops seeding it.
  const [demoTouched, setDemoTouched] = useState(false);

  const save = useSaveLabRow<Partial<LabConfiguration> & { id?: number }>(LAB_TABLES.configuration);

  const { data: labs } = useLabs();
  const { data: dataCenters } = useLabDataCenters();

  /**
   * A lab created from inside this form, held until the catalogue query catches up.
   *
   * Not a convenience: the slug and the suggested name are both derived from the *lab row*, so a
   * publication saved in the gap between the nested create and the refetch would be named from
   * nothing and slugged from the name instead of the pairing.
   */
  const [createdLab, setCreatedLab] = useState<Lab | null>(null);
  const [newLab, setNewLab] = useState(false);

  const selectedLab = useMemo(
    () =>
      (labs || []).find((entry) => entry.id === draft.lab) ||
      (createdLab && createdLab.id === draft.lab ? createdLab : null),
    [labs, createdLab, draft.lab],
  );

  const selectedDataCenter = useMemo(
    () => (dataCenters || []).find((entry) => entry.id === draft.data_center) || null,
    [dataCenters, draft.data_center],
  );
  const dataCenterCode = labDataCenterCode(selectedDataCenter);

  useEffect(() => {
    if (!open) return;

    setDraft(
      configuration ? { ...configuration } : { ...EMPTY, ...(defaultLabId ? { lab: defaultLabId } : {}) },
    );
    setJsonError(null);
    setFormError(null);
    setAdvancedOpen(false);
    setNameTouched(false);
    setDemoTouched(false);
    setNewLab(false);
    setCreatedLab(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, configuration?.id, defaultLabId]);

  const set = (patch: Partial<LabConfiguration>) => setDraft((current) => ({ ...current, ...patch }));

  /**
   * The slug this row will carry.
   *
   * Built from the lab and the data centre rather than from the name: `slug` is unique across the
   * whole table while a name is free text, and the pair it is built from is exactly the pair the
   * table is unique on - so a slug collision here is a duplicate the form already refuses by
   * name. An existing row keeps the slug it was saved with, whatever it is.
   */
  const generatedSlug = useMemo(() => {
    if (configuration) return configuration.slug;

    return (
      labConfigurationSlug(selectedLab?.slug || selectedLab?.name, selectedDataCenter) ||
      labSlugify(draft.name || '')
    );
  }, [configuration, selectedLab, selectedDataCenter, draft.name]);

  /**
   * Seeds the demo from the lab when this publication is in the lab's own data centre.
   *
   * The lab records the demo it was authored against and which data centre that id was read in. If
   * this publication is for that data centre then its demo id is already known, and asking somebody
   * to look it up again is asking them to retype what the lab says - with a chance of retyping it
   * wrong. Anywhere else it is left alone: dCloud numbers content per data centre, so the lab's id
   * is not this data centre's id and carrying it over would be a guess that looks authored.
   *
   * New rows only, and only until somebody picks or types one themselves.
   */
  useEffect(() => {
    if (!open || configuration || demoTouched) return;
    if (!selectedLab || !draft.data_center) return;
    if (selectedLab.demo_uid === null || selectedLab.demo_uid === undefined) return;
    if (selectedLab.demo_data_center !== draft.data_center) return;
    if (draft.demo_uid !== null && draft.demo_uid !== undefined) return;

    set({ demo_uid: selectedLab.demo_uid, demo_name: selectedLab.demo_name || '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, configuration, demoTouched, selectedLab?.id, draft.data_center, draft.demo_uid]);

  /** Suggests a name once both halves of the pairing are known. Typing over it wins. */
  useEffect(() => {
    if (!open || configuration || nameTouched) return;
    if (!selectedLab || !selectedDataCenter) return;

    set({ name: `${selectedLab.name} - ${selectedDataCenter.dcloud_dc_name}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, configuration, nameTouched, selectedLab?.id, selectedDataCenter?.id]);

  /**
   * The labs that can be published.
   *
   * A lab with no sections is offered rather than hidden: publishing it is harmless and the
   * hint says what it would deliver, which is nothing. Hiding it would leave an admin who has
   * just created a lab wondering why it is not in the list.
   */
  const labOptions: LabOption<string>[] = useMemo(() => {
    const rows = [...(labs || [])];

    // A lab created a moment ago from inside this form, before the catalogue query has caught up.
    // Without this the select shows an empty box over a lab that exists and is already chosen.
    if (createdLab && !rows.some((lab) => lab.id === createdLab.id)) rows.push(createdLab);

    return rows.map((lab) => {
      const sections = (lab.scenario_links || []).filter((link) => link.enabled).length;

      return {
        value: String(lab.id),
        label: lab.name,
        hint: !lab.enabled
          ? 'This lab is disabled and is not matched against sessions.'
          : sections
            ? `${sections} section${sections === 1 ? '' : 's'}`
            : 'No sections attached yet, so it would deliver the bare template.',
      };
    });
  }, [labs, createdLab]);

  const dataCenterOptions: LabOption<string>[] = useMemo(
    () =>
      (dataCenters || []).map((entry) => ({
        value: String(entry.id),
        label: entry.dcloud_dc_name,
        hint: entry.dcloud_dc_region || undefined,
      })),
    [dataCenters],
  );

  /**
   * A pick from the lookup, applied as a patch.
   *
   * It only ever carries dCloud's own columns; the data centre stays the form's, which is why the
   * lookup reads the one selected above rather than offering its own. A demo id is only valid in
   * the data centre it was read from, and two controls for that is two ways to disagree.
   */
  const applyPick = (pick: LabDcloudPick) => {
    set(pick as Partial<LabConfiguration>);
    if (pick.demo_uid !== undefined) setDemoTouched(true);
    setFormError(null);
  };

  /**
   * The lab says it is built from one demo in this data centre and the publication names another.
   *
   * Only reported where the two are talking about the same data centre - anywhere else they are
   * *expected* to differ, which is why both columns exist. A session matches on the publication's
   * id whatever the lab says, so this is the lab's link being stale or the publication pointing at
   * the wrong content, and neither is discoverable once a pod is up and nothing applies to it.
   */
  const labDemoMismatch =
    !!selectedLab &&
    selectedLab.demo_uid !== null &&
    selectedLab.demo_uid !== undefined &&
    !!draft.data_center &&
    selectedLab.demo_data_center === draft.data_center &&
    draft.demo_uid !== null &&
    draft.demo_uid !== undefined &&
    draft.demo_uid !== selectedLab.demo_uid;

  const handleSubmit = async () => {
    if (!draft.lab) {
      setFormError('Pick the lab this publishes.');
      return;
    }

    if (!draft.data_center) {
      setFormError('Pick the data centre it runs in. That pairing is what this row is.');
      return;
    }

    if (!draft.name?.trim()) {
      setFormError('Give the publication a name.');
      return;
    }

    if (!generatedSlug) {
      setFormError(
        'No slug could be derived from that lab and data centre. Give the lab a slug first.',
      );
      return;
    }

    const clash = existing.find(
      (candidate) =>
        candidate.id !== draft.id &&
        candidate.lab === draft.lab &&
        candidate.data_center === draft.data_center,
    );

    if (clash) {
      setFormError(
        `That lab is already published in that data centre as "${clash.name}". Edit that one instead - one publication per data centre is the whole point of the row.`,
      );
      return;
    }

    if (jsonError) {
      setFormError(`Metadata: ${jsonError}`);
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, slug: generatedSlug });
      onSaved?.(saved as LabConfiguration);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  const hasDcloudIdentity = draft.demo_uid !== null && draft.demo_uid !== undefined;

  return (
    <StbModal
      open={open}
      role="update"
      title={configuration ? `Edit ${configuration.name}` : 'Publish a lab'}
      size="large"
      scrollable
      submitLabel={configuration ? 'Save publication' : 'Publish'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {/* A publication cannot be the first thing authored, and saying so beats an empty
            dropdown that reads like a loading failure. */}
        {!labOptions.length && (
          <p className="m-0 text-sm text-amber-800">
            There are no labs yet, and this publishes one. Close this and create a lab first - the
            lab is what gets delivered, and it is authored once for every data centre it runs in.
          </p>
        )}

        {/* A row written before the lab tier existed named a scenario directly. It still runs, so
            it is editable, but giving it a lab is the only way it gains sections - and the reader
            needs to know which of the two bindings they are looking at. */}
        {configuration && !configuration.lab && configuration.scenario && (
          <p className="m-0 text-sm text-amber-800">
            This publication predates the lab tier: it names the scenario{' '}
            {configuration.scenario_name || configuration.scenario} directly rather than a lab.
            Pick a lab below to bring it forward - the sections that lab composes are what it will
            deliver from then on.
          </p>
        )}

        <LabFieldRow>
          <div className="flex flex-col gap-1">
            <LabOptionSelect
              label="Lab"
              value={draft.lab ? String(draft.lab) : ''}
              options={labOptions}
              allowEmpty
              emptyLabel="Pick a lab"
              onChange={(value) => set({ lab: value ? Number(value) : null })}
              helpText="What is delivered. Every section it composes runs in this one pod, which is why the publication hangs here."
            />
            <div>
              <StbButton role="add" size="xsmall" onClick={async () => setNewLab(true)}>
                + New lab
              </StbButton>
            </div>
          </div>
          <LabOptionSelect
            label="Data centre"
            value={draft.data_center ? String(draft.data_center) : ''}
            options={dataCenterOptions}
            allowEmpty
            emptyLabel="Pick a data centre"
            onChange={(value) => set({ data_center: value ? Number(value) : undefined })}
            helpText="One publication per lab and data centre. The same lab has a different demo id in each."
          />
        </LabFieldRow>

        <TextField
          label="Publication name"
          value={draft.name || ''}
          onChange={(evt) => {
            setNameTouched(true);
            set({ name: evt.target.value });
          }}
          helperText="What this is called wherever it is scheduled. The rest is derived or looked up."
          fullWidth
        />

        {/* Shown rather than hidden: it is what events and runs record, so an admin chasing one
            needs to be able to read it - just not to invent it. */}
        <LabDerivedKey
          value={generatedSlug}
          frozen={!!configuration}
          label="Slug"
          usage="Events and runs record the publication by it."
          emptyLabel="derived from the lab and data centre"
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          multiline
          minRows={2}
          fullWidth
        />

        {/* The dCloud identity, read-only. This is the one thing on the form that has to come
            from somewhere else, and the lookup below is that somewhere. */}
        <div className="rounded-md border border-gray-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-gray-700">dCloud lab</span>
            {hasDcloudIdentity ? (
              <LabStatusTag label={`demo ${draft.demo_uid}`} sentiment="success" />
            ) : (
              <LabStatusTag label="Not identified" sentiment="warning" />
            )}
          </div>

          {hasDcloudIdentity ? (
            <dl className="m-0 mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
              <div>
                <dt className="inline text-gray-500">Demo id: </dt>
                <dd className="m-0 inline font-mono">{draft.demo_uid}</dd>
              </div>
              <div>
                <dt className="inline text-gray-500">Demo name: </dt>
                <dd className="m-0 inline">{draft.demo_name || '-'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="inline text-gray-500">Template: </dt>
                <dd className="m-0 inline font-mono break-all">
                  {draft.template_topology_uid || 'not set'}
                </dd>
              </div>
              <div>
                <dt className="inline text-gray-500">Size: </dt>
                <dd className="m-0 inline">{labSizeLabel(draft.size)}</dd>
              </div>
            </dl>
          ) : (
            <p className="m-0 mt-2 text-xs text-amber-800">
              Until the demo id is set, no session in this data centre is ever matched to this lab.
              Find it in the lookup below.
            </p>
          )}

          {labDemoMismatch && (
            <p className="m-0 mt-2 text-xs text-amber-800">
              {`${selectedLab?.name} says it is built from demo ${selectedLab?.demo_uid} in this data centre, and this publication names demo ${draft.demo_uid}. Sessions are matched on this row's id, so if the lab's link is the right one, fix it here - or update the lab.`}
            </p>
          )}

          {!!draft.data_center && !dataCenterCode && (
            <p className="m-0 mt-2 text-xs text-amber-800">
              No dCloud code recognised for that data centre, so its catalogs cannot be listed. Use
              &quot;Enter the dCloud ids by hand&quot; below.
            </p>
          )}
        </div>

        {/* Inline rather than a second modal: this codebase stacks modals nowhere else, and the
            lookup is a step in filling this form rather than a detour away from it. It is the
            only place the lookup lives - it answers one question, and this is where it is asked. */}
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <LabDcloudLookupPanel dataCenterId={draft.data_center ?? null} onPick={applyPick} />
        </div>

        <LabCheckbox
          label="Enabled"
          checked={!!draft.enabled}
          onChange={(enabled) => set({ enabled })}
          helpText="A disabled publication is not matched against sessions in this data centre at all. The lab keeps running wherever else it is published."
        />

        <div>
          <StbButton
            role="default"
            size="small"
            onClick={async () => setAdvancedOpen(!advancedOpen)}
          >
            {advancedOpen ? 'Hide manual entry' : 'Enter the dCloud ids by hand'}
          </StbButton>
          <p className="m-0 mt-1 text-xs text-gray-500">
            For a data centre whose catalog cannot be read, or a template the lookup could not
            size. Everything here is otherwise filled in by a pick above.
          </p>
        </div>

        {advancedOpen && (
          <div className="flex flex-col gap-4 rounded-md border border-gray-200 p-3">
            <LabFieldRow>
              <TextField
                label="dCloud demo id"
                type="number"
                value={draft.demo_uid ?? ''}
                onChange={(evt) => {
                  setDemoTouched(true);
                  set({ demo_uid: evt.target.value === '' ? null : Number(evt.target.value) });
                }}
                helperText="What a session in this data centre reports as its parent."
                fullWidth
              />
              <TextField
                label="Demo name"
                value={draft.demo_name || ''}
                onChange={(evt) => set({ demo_name: evt.target.value })}
                helperText="Fallback match when the id is absent, and what a person reads in a list."
                fullWidth
              />
            </LabFieldRow>

            <LabFieldRow>
              <TextField
                label="Template topology uid"
                value={draft.template_topology_uid || ''}
                onChange={(evt) => set({ template_topology_uid: evt.target.value })}
                helperText="The pre-configured pod this lab is layered on top of."
                fullWidth
              />
              <TextField
                label="Topology uid"
                value={draft.topology_uid || ''}
                onChange={(evt) => set({ topology_uid: evt.target.value })}
                helperText="The published topology, if this lab has one of its own."
                fullWidth
              />
            </LabFieldRow>

            <LabOptionSelect
              label="Template size"
              value={(draft.size as LabSize) || 'medium'}
              options={LAB_SIZES}
              onChange={(size) => set({ size })}
              helpText="The capacity the template was published at. It decides what a pod can hold, and it differs per data centre."
            />

            <LabSpecBuilder
              schema={LAB_JSON_SCHEMAS.configurationMetadata}
              value={draft.metadata}
              resetKey={configuration?.id ?? 'new'}
              onChange={(metadata, error) => {
                setJsonError(error);
                if (!error) set({ metadata });
              }}
            />
          </div>
        )}
      </div>

      {/* Over this form rather than instead of it: publishing is usually the next thing after
          creating a lab, and an admin who has arrived here to find the lab missing should not have
          to abandon the dCloud lookup they have already done. */}
      <LabEditorModal
        open={newLab}
        lab={null}
        existing={labs || []}
        onClose={() => setNewLab(false)}
        onSaved={(saved) => {
          setCreatedLab(saved);
          set({ lab: saved.id });
        }}
      />
    </StbModal>
  );
};

export default LabConfigurationEditorModal;
